/**
 * MoneyText and DurationText — the two formatted-number primitives.
 *
 * MoneyText is used by: order and payment lists, the cash drawer screen, the
 * product editor, dashboard KPI tiles, payroll previews.
 *
 * IT TAKES MINOR UNITS, ALWAYS. Every money value on the wire is an integer
 * count of minor units paired with a currency and an exponent (see
 * `@sonauto/utilities/money`) — VND has exponent 0, so 25000 is 25.000 ₫, not
 * 250 ₫. Passing a float here is a bug, and the type makes that explicit.
 * Formatting is delegated to `formatMoney`, so display and arithmetic can never
 * disagree about rounding.
 *
 * DurationText is used by: KDS ticket ages, SLA countdowns, shift lengths,
 * "chờ bao lâu" columns. It takes SECONDS.
 */
import { formatMoney, money } from '@sonauto/utilities';
import { cn } from '../utils.js';

export interface MoneyTextProps {
  /** Integer count of minor units. Never a float. */
  readonly amountMinor: number;
  readonly currency?: string;
  readonly exponent?: number;
  readonly locale?: string;
  /** Colour negatives red and positives green (used in cash movements). */
  readonly signed?: boolean;
  /** Render "—" for null/undefined instead of 0 ₫, which would be a lie. */
  readonly emptyText?: string;
  readonly className?: string;
}

export function MoneyText({
  amountMinor,
  currency = 'VND',
  exponent = 0,
  locale = 'vi-VN',
  signed = false,
  className,
}: MoneyTextProps): React.JSX.Element {
  const text = formatMoney(money(Math.trunc(amountMinor), currency, exponent), locale);
  return (
    <span
      className={cn(
        'sa-tabular whitespace-nowrap',
        signed && amountMinor < 0 && 'text-danger-fg',
        signed && amountMinor > 0 && 'text-success-fg',
        className,
      )}
    >
      {signed && amountMinor > 0 ? '+' : ''}
      {text}
    </span>
  );
}

/** Nullable variant so a screen does not have to branch inline. */
export function MoneyTextOrDash({
  amountMinor,
  emptyText = '—',
  ...rest
}: Omit<MoneyTextProps, 'amountMinor'> & {
  readonly amountMinor: number | null | undefined;
}): React.JSX.Element {
  if (amountMinor === null || amountMinor === undefined) {
    return <span className="text-fg-subtle">{emptyText}</span>;
  }
  return <MoneyText amountMinor={amountMinor} {...rest} />;
}

export interface DurationTextProps {
  /** Elapsed seconds. */
  readonly seconds: number | null | undefined;
  /** `short` = "12p 30g", `clock` = "12:30", `long` = "12 phút 30 giây". */
  readonly format?: 'short' | 'clock' | 'long';
  /** Turn amber/red past these thresholds — used for SLA columns. */
  readonly warnAfterSeconds?: number;
  readonly dangerAfterSeconds?: number;
  readonly className?: string;
}

export function DurationText({
  seconds,
  format = 'short',
  warnAfterSeconds,
  dangerAfterSeconds,
  className,
}: DurationTextProps): React.JSX.Element {
  if (seconds === null || seconds === undefined) return <span className="text-fg-subtle">—</span>;

  const total = Math.max(0, Math.trunc(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  let text: string;
  if (format === 'clock') {
    text = h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  } else if (format === 'long') {
    const parts: string[] = [];
    if (h > 0) parts.push(`${h} giờ`);
    if (m > 0) parts.push(`${m} phút`);
    if (h === 0 && s > 0) parts.push(`${s} giây`);
    text = parts.length === 0 ? '0 giây' : parts.join(' ');
  } else {
    text = h > 0 ? `${h}g ${m}p` : m > 0 ? `${m}p ${pad(s)}g` : `${s}g`;
  }

  const danger = dangerAfterSeconds !== undefined && total >= dangerAfterSeconds;
  const warn = !danger && warnAfterSeconds !== undefined && total >= warnAfterSeconds;

  return (
    <span
      className={cn(
        'sa-tabular whitespace-nowrap',
        warn && 'font-medium text-warning-fg',
        danger && 'font-semibold text-danger-fg',
        className,
      )}
    >
      {text}
    </span>
  );
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}
